/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                                                                           ║
 * ║                 🎮 WORMCOSMOS - CLIENT CONFIGURATION 🎮                   ║
 * ║                                                                           ║
 * ║  This file contains client-only settings for rendering, UI, effects,      ║
 * ║  and visual presentation. Game logic constants are in @wooorms/shared.    ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          📊 USER INTERFACE                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Minimap configuration
 */
export const UI_MINIMAP = {
  /** Size as percentage of screen width (0.18 = 18%) */
  SIZE_PERCENT: 0.18,
} as const;

export const MINIMAP_SIZE_PERCENT = UI_MINIMAP.SIZE_PERCENT;

/**
 * Leaderboard configuration
 */
export const UI_LEADERBOARD = {
  /** Maximum entries to display */
  MAX_ENTRIES: 10,
  /** Panel width in pixels */
  PANEL_WIDTH: 180,
} as const;

export const LEADERBOARD_MAX_ENTRIES = UI_LEADERBOARD.MAX_ENTRIES;
export const LEADERBOARD_PANEL_WIDTH = UI_LEADERBOARD.PANEL_WIDTH;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          📷 CAMERA SETTINGS                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Camera zoom and control settings
 */
export const CAMERA = {
  /** Zoom change per mouse wheel tick */
  ZOOM_DELTA: 0.1,
  /** Minimum segment count where zoom starts to scale */
  ZOOM_SIZE_MIN_SEGMENTS: 10,
  /** Maximum segment count where zoom reaches minimum value (scales up to 1M score) */
  ZOOM_SIZE_MAX_SEGMENTS: 10000,
  /** Zoom level for small worms (close up, higher = see less map) */
  ZOOM_MAX: 1.4,
  /** Zoom level for large worms (zoomed out to see more - supports mega worms) */
  ZOOM_MIN_AT_SIZE: 0.2,
} as const;

export const CAMERA_ZOOM_DELTA = CAMERA.ZOOM_DELTA;
export const CAMERA_ZOOM_SIZE_MIN_SEGMENTS = CAMERA.ZOOM_SIZE_MIN_SEGMENTS;
export const CAMERA_ZOOM_SIZE_MAX_SEGMENTS = CAMERA.ZOOM_SIZE_MAX_SEGMENTS;
export const CAMERA_ZOOM_MAX = CAMERA.ZOOM_MAX;
export const CAMERA_ZOOM_MIN_AT_SIZE = CAMERA.ZOOM_MIN_AT_SIZE;

/**
 * Screen shake effect settings
 */
export const SCREEN_SHAKE = {
  /** Maximum shake intensity in pixels */
  MAX_INTENSITY: 10,
  /** Decay rate (how quickly it fades) */
  DECAY_RATE: 5,
  /** Oscillation frequency per second */
  FREQUENCY: 30,
  /** Warning intensity threshold to trigger shake (0-1) */
  WARNING_THRESHOLD: 0.5,
} as const;

export const SCREEN_SHAKE_MAX_INTENSITY = SCREEN_SHAKE.MAX_INTENSITY;
export const SCREEN_SHAKE_DECAY_RATE = SCREEN_SHAKE.DECAY_RATE;
export const SCREEN_SHAKE_FREQUENCY = SCREEN_SHAKE.FREQUENCY;
export const SCREEN_SHAKE_WARNING_THRESHOLD = SCREEN_SHAKE.WARNING_THRESHOLD;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          🐛 WORM RENDERING                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Worm visual effects
 */
export const WORM_RENDERING = {
  /** Default glow intensity (0-1) */
  GLOW_INTENSITY_DEFAULT: 0.4,
  /** Enhanced glow for online worms */
  GLOW_INTENSITY_ONLINE: 0.5,
} as const;

export const WORM_GLOW_INTENSITY_DEFAULT = WORM_RENDERING.GLOW_INTENSITY_DEFAULT;
export const WORM_GLOW_INTENSITY_ONLINE = WORM_RENDERING.GLOW_INTENSITY_ONLINE;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  📋 Offline mode settings are in ./offline-config.ts                      ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        🎯 INPUT & NETWORK                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Input handling settings
 */
export const INPUT = {
  /** Input send rate to server (messages per second) */
  SEND_RATE: 20,
  /** Mouse button for boost activation (0 = left click) */
  BOOST_BUTTON: 0,
} as const;

export const INPUT_SEND_RATE = INPUT.SEND_RATE;
export const BOOST_MOUSE_BUTTON = INPUT.BOOST_BUTTON;

/**
 * Collision and interaction settings
 */
export const COLLISION = {
  /**
   * Base offset added to worm head radius for food pickup detection.
   * Pickup Range = Head Radius + BASE_FOOD_PICKUP_OFFSET
   * Same for ALL worms (player and AI) - fair for everyone.
   */
  BASE_FOOD_PICKUP_OFFSET: 80,
  /**
   * Forward offset for eating - positions the eating zone ahead of the head
   * This is a multiplier of the head radius (e.g., 0.8 = 80% of head radius forward)
   */
  EATING_FORWARD_OFFSET: 0.8,
  /** Boundary padding for worm movement clamping */
  BOUNDARY_PADDING: 20,
} as const;

export const BASE_FOOD_PICKUP_OFFSET = COLLISION.BASE_FOOD_PICKUP_OFFSET;
export const EATING_FORWARD_OFFSET = COLLISION.EATING_FORWARD_OFFSET;
export const BOUNDARY_PADDING = COLLISION.BOUNDARY_PADDING;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        🌍 WORLD RENDERING                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * World background and grid settings
 */
export const WORLD_RENDERING = {
  /** Background grid cell size */
  GRID_SIZE: 100,
  /** Grid line color */
  GRID_COLOR: 0x2a2a4a,
  /** Grid line alpha */
  GRID_ALPHA: 0.5,
  /** World background color */
  BACKGROUND_COLOR: 0x0a0a1a,
  /** Border danger zone color */
  BORDER_COLOR: 0xff4444,
  /** Border glow warning color */
  BORDER_GLOW_COLOR: 0xff0000,
  /** Distance from border to start warning */
  WARNING_DISTANCE: 300,
  /** Origin marker radius */
  ORIGIN_MARKER_RADIUS: 5,
  /** Origin marker color */
  ORIGIN_MARKER_COLOR: 0x4ecdc4,
  /** Origin marker alpha */
  ORIGIN_MARKER_ALPHA: 0.5,
} as const;

export const WORLD_GRID_SIZE = WORLD_RENDERING.GRID_SIZE;
export const WORLD_GRID_COLOR = WORLD_RENDERING.GRID_COLOR;
export const WORLD_GRID_ALPHA = WORLD_RENDERING.GRID_ALPHA;
export const WORLD_BACKGROUND_COLOR = WORLD_RENDERING.BACKGROUND_COLOR;
export const WORLD_BORDER_COLOR = WORLD_RENDERING.BORDER_COLOR;
export const WORLD_BORDER_GLOW_COLOR = WORLD_RENDERING.BORDER_GLOW_COLOR;
export const WORLD_WARNING_DISTANCE = WORLD_RENDERING.WARNING_DISTANCE;
export const WORLD_ORIGIN_MARKER_RADIUS = WORLD_RENDERING.ORIGIN_MARKER_RADIUS;
export const WORLD_ORIGIN_MARKER_COLOR = WORLD_RENDERING.ORIGIN_MARKER_COLOR;
export const WORLD_ORIGIN_MARKER_ALPHA = WORLD_RENDERING.ORIGIN_MARKER_ALPHA;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      🐍 GIANT SNAKE (WORLD BORDER)                        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Giant snake border animation settings
 */
export const GIANT_SNAKE = {
  // Colors
  /** Primary body color (dark green) */
  PRIMARY_COLOR: 0x2d5a27,
  /** Secondary body color (lighter green) */
  SECONDARY_COLOR: 0x4a8f3c,
  /** Eye color */
  EYE_COLOR: 0xffff00,
  /** Belly color */
  BELLY_COLOR: 0x8fbc8f,
  /** Scale spot color */
  SCALE_SPOT_COLOR: 0x1a3d16,
  /** Tongue color */
  TONGUE_COLOR: 0xff3333,

  // Geometry
  /** Number of body segments around the circle */
  SEGMENT_COUNT: 120,
  /** Width multiplier relative to border thickness */
  WIDTH_MULTIPLIER: 1.8,
  /** Scale pattern size multiplier */
  SCALE_SIZE_MULTIPLIER: 0.4,
  /** Number of tail segments */
  TAIL_SEGMENTS: 8,

  // Animation
  /** Body undulation magnitude */
  UNDULATION_MAGNITUDE: 8,
  /** Undulation frequency */
  UNDULATION_FREQUENCY: 8,
  /** Animation speed multiplier */
  ANIMATION_SPEED: 0.5,
  /** Tongue animation speed */
  TONGUE_ANIMATION_SPEED: 3,
} as const;

export const SNAKE_PRIMARY_COLOR = GIANT_SNAKE.PRIMARY_COLOR;
export const SNAKE_SECONDARY_COLOR = GIANT_SNAKE.SECONDARY_COLOR;
export const SNAKE_EYE_COLOR = GIANT_SNAKE.EYE_COLOR;
export const SNAKE_BELLY_COLOR = GIANT_SNAKE.BELLY_COLOR;
export const SNAKE_SCALE_SPOT_COLOR = GIANT_SNAKE.SCALE_SPOT_COLOR;
export const SNAKE_TONGUE_COLOR = GIANT_SNAKE.TONGUE_COLOR;
export const SNAKE_SEGMENT_COUNT = GIANT_SNAKE.SEGMENT_COUNT;
export const SNAKE_WIDTH_MULTIPLIER = GIANT_SNAKE.WIDTH_MULTIPLIER;
export const SNAKE_SCALE_SIZE_MULTIPLIER = GIANT_SNAKE.SCALE_SIZE_MULTIPLIER;
export const SNAKE_UNDULATION_MAGNITUDE = GIANT_SNAKE.UNDULATION_MAGNITUDE;
export const SNAKE_UNDULATION_FREQUENCY = GIANT_SNAKE.UNDULATION_FREQUENCY;
export const SNAKE_ANIMATION_SPEED = GIANT_SNAKE.ANIMATION_SPEED;
export const SNAKE_TONGUE_ANIMATION_SPEED = GIANT_SNAKE.TONGUE_ANIMATION_SPEED;
export const SNAKE_TAIL_SEGMENTS = GIANT_SNAKE.TAIL_SEGMENTS;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                         ✨ VISUAL EFFECTS                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Glow and blur effect settings
 */
export const GLOW_EFFECTS = {
  /** Blur filter strength */
  BLUR_STRENGTH: 8,
  /** Blur filter quality (higher = smoother) */
  BLUR_QUALITY: 4,
  /** Border glow pulse period (ms) */
  PULSE_PERIOD: 200,
  /** Pulse intensity amplitude */
  PULSE_AMPLITUDE: 0.3,
  /** Pulse center value */
  PULSE_CENTER: 0.7,
  /** Glow width multiplier */
  WIDTH_MULTIPLIER: 3,
  /** Inner danger line alpha */
  INNER_LINE_ALPHA: 0.2,
} as const;

export const GLOW_BLUR_STRENGTH = GLOW_EFFECTS.BLUR_STRENGTH;
export const GLOW_BLUR_QUALITY = GLOW_EFFECTS.BLUR_QUALITY;
export const BORDER_GLOW_PULSE_PERIOD = GLOW_EFFECTS.PULSE_PERIOD;
export const BORDER_GLOW_PULSE_AMPLITUDE = GLOW_EFFECTS.PULSE_AMPLITUDE;
export const BORDER_GLOW_PULSE_CENTER = GLOW_EFFECTS.PULSE_CENTER;
export const BORDER_GLOW_WIDTH_MULTIPLIER = GLOW_EFFECTS.WIDTH_MULTIPLIER;
export const INNER_DANGER_LINE_ALPHA = GLOW_EFFECTS.INNER_LINE_ALPHA;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        💥 PARTICLE SYSTEM                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Core particle system settings
 */
export const PARTICLES = {
  /** Maximum particles in the system */
  MAX_COUNT: 500,
  /** Default particle lifetime (seconds) */
  DEFAULT_LIFETIME: 0.5,
} as const;

export const PARTICLE_MAX_COUNT = PARTICLES.MAX_COUNT;
export const PARTICLE_DEFAULT_LIFETIME = PARTICLES.DEFAULT_LIFETIME;

/**
 * Food collection particle effects
 */
export const FOOD_PARTICLES = {
  /** Particle count per food collected */
  COUNT: 8,
  /** Speed range */
  SPEED_MIN: 80,
  SPEED_MAX: 150,
  /** Size range */
  SIZE_START: 4,
  SIZE_END: 1,
  /** Lifetime (seconds) */
  LIFETIME: 0.4,
  /** Gravity (pixels/sec²) */
  GRAVITY: 200,
} as const;

export const FOOD_PARTICLE_COUNT = FOOD_PARTICLES.COUNT;
export const FOOD_PARTICLE_SPEED_MIN = FOOD_PARTICLES.SPEED_MIN;
export const FOOD_PARTICLE_SPEED_MAX = FOOD_PARTICLES.SPEED_MAX;
export const FOOD_PARTICLE_SIZE_START = FOOD_PARTICLES.SIZE_START;
export const FOOD_PARTICLE_SIZE_END = FOOD_PARTICLES.SIZE_END;
export const FOOD_PARTICLE_LIFETIME = FOOD_PARTICLES.LIFETIME;
export const FOOD_PARTICLE_GRAVITY = FOOD_PARTICLES.GRAVITY;

/**
 * Powerup collection particle effects
 */
export const POWERUP_PARTICLES = {
  /** Particle count per powerup */
  COUNT: 16,
  /** Speed range */
  SPEED_MIN: 100,
  SPEED_MAX: 200,
  /** Lifetime (seconds) */
  LIFETIME: 0.6,
} as const;

export const POWERUP_PARTICLE_COUNT = POWERUP_PARTICLES.COUNT;
export const POWERUP_PARTICLE_SPEED_MIN = POWERUP_PARTICLES.SPEED_MIN;
export const POWERUP_PARTICLE_SPEED_MAX = POWERUP_PARTICLES.SPEED_MAX;
export const POWERUP_PARTICLE_LIFETIME = POWERUP_PARTICLES.LIFETIME;

/**
 * Death explosion particle effects
 */
export const DEATH_PARTICLES = {
  /** Primary particle count */
  COUNT: 24,
  /** Spark count */
  SPARK_COUNT: 16,
  /** Ring count (expanding circles) */
  RING_COUNT: 3,
  /** Main particle speed */
  SPEED_MIN: 150,
  SPEED_MAX: 350,
  /** Main particle size */
  SIZE_START: 10,
  SIZE_END: 2,
  /** Main particle lifetime */
  LIFETIME: 0.8,
  /** Spark speed */
  SPARK_SPEED_MIN: 200,
  SPARK_SPEED_MAX: 500,
  /** Spark lifetime */
  SPARK_LIFETIME: 0.5,
} as const;

export const DEATH_PARTICLE_COUNT = DEATH_PARTICLES.COUNT;
export const DEATH_SPARK_COUNT = DEATH_PARTICLES.SPARK_COUNT;
export const DEATH_RING_COUNT = DEATH_PARTICLES.RING_COUNT;
export const DEATH_PARTICLE_SPEED_MIN = DEATH_PARTICLES.SPEED_MIN;
export const DEATH_PARTICLE_SPEED_MAX = DEATH_PARTICLES.SPEED_MAX;
export const DEATH_PARTICLE_SIZE_START = DEATH_PARTICLES.SIZE_START;
export const DEATH_PARTICLE_SIZE_END = DEATH_PARTICLES.SIZE_END;
export const DEATH_PARTICLE_LIFETIME = DEATH_PARTICLES.LIFETIME;
export const DEATH_SPARK_SPEED_MIN = DEATH_PARTICLES.SPARK_SPEED_MIN;
export const DEATH_SPARK_SPEED_MAX = DEATH_PARTICLES.SPARK_SPEED_MAX;
export const DEATH_SPARK_LIFETIME = DEATH_PARTICLES.SPARK_LIFETIME;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                       💀 KILL & COMBO EFFECTS                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Kill text popup settings
 */
export const KILL_TEXT = {
  /** Duration on screen (seconds) */
  DURATION: 1.5,
  /** Rise speed (pixels per second) */
  RISE_SPEED: 60,
} as const;

export const KILL_TEXT_DURATION = KILL_TEXT.DURATION;
export const KILL_TEXT_RISE_SPEED = KILL_TEXT.RISE_SPEED;

/**
 * Combo system settings
 */
export const COMBO = {
  /** Time window to maintain combo (seconds) */
  TIME_WINDOW: 3.0,
  /** Minimum kills to trigger combo display */
  MIN_KILLS: 2,
  /** Score bonus per combo level */
  BONUS_MULTIPLIER: 50,
  /** Y offset for combo popup above kill text */
  POPUP_Y_OFFSET: 40,
} as const;

export const COMBO_TIME_WINDOW = COMBO.TIME_WINDOW;
export const COMBO_MIN_KILLS = COMBO.MIN_KILLS;
export const COMBO_BONUS_MULTIPLIER = COMBO.BONUS_MULTIPLIER;
export const COMBO_POPUP_Y_OFFSET = COMBO.POPUP_Y_OFFSET;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                     🌊 MOVEMENT TRAILS & BOOST                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Trail segment settings
 */
export const TRAILS = {
  /** Spawn interval (seconds between trail points) */
  SPAWN_INTERVAL: 0.03,
  /** Normal trail lifetime (seconds) */
  LIFETIME: 0.4,
  /** Boosting trail lifetime (longer) */
  LIFETIME_BOOST: 0.6,
  /** Size multiplier relative to worm radius */
  SIZE_MULTIPLIER: 0.8,
  /** Size multiplier when boosting (thicker) */
  SIZE_MULTIPLIER_BOOST: 1.2,
  /** Maximum trail segments */
  MAX_SEGMENTS: 100,
} as const;

export const TRAIL_SPAWN_INTERVAL = TRAILS.SPAWN_INTERVAL;
export const TRAIL_LIFETIME = TRAILS.LIFETIME;
export const TRAIL_LIFETIME_BOOST = TRAILS.LIFETIME_BOOST;
export const TRAIL_SIZE_MULTIPLIER = TRAILS.SIZE_MULTIPLIER;
export const TRAIL_SIZE_MULTIPLIER_BOOST = TRAILS.SIZE_MULTIPLIER_BOOST;
export const TRAIL_MAX_SEGMENTS = TRAILS.MAX_SEGMENTS;

/**
 * Boost particle and speed line effects
 */
export const BOOST_EFFECTS = {
  /** Particle spawn rate (per second) */
  PARTICLE_RATE: 30,
  /** Particle speed range */
  PARTICLE_SPEED_MIN: 50,
  PARTICLE_SPEED_MAX: 120,
  /** Particle size */
  PARTICLE_SIZE: 4,
  /** Particle lifetime */
  PARTICLE_LIFETIME: 0.3,
  /** Speed lines count */
  SPEED_LINE_COUNT: 6,
  /** Speed line length range */
  SPEED_LINE_LENGTH_MIN: 20,
  SPEED_LINE_LENGTH_MAX: 50,
} as const;

export const BOOST_PARTICLE_RATE = BOOST_EFFECTS.PARTICLE_RATE;
export const BOOST_PARTICLE_SPEED_MIN = BOOST_EFFECTS.PARTICLE_SPEED_MIN;
export const BOOST_PARTICLE_SPEED_MAX = BOOST_EFFECTS.PARTICLE_SPEED_MAX;
export const BOOST_PARTICLE_SIZE = BOOST_EFFECTS.PARTICLE_SIZE;
export const BOOST_PARTICLE_LIFETIME = BOOST_EFFECTS.PARTICLE_LIFETIME;
export const BOOST_SPEED_LINE_COUNT = BOOST_EFFECTS.SPEED_LINE_COUNT;
export const BOOST_SPEED_LINE_LENGTH_MIN = BOOST_EFFECTS.SPEED_LINE_LENGTH_MIN;
export const BOOST_SPEED_LINE_LENGTH_MAX = BOOST_EFFECTS.SPEED_LINE_LENGTH_MAX;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                     🍎 FOOD RENDERING - RETRO ARCADE                      ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Food visual effects for that retro arcade feel
 */
export const FOOD_RENDERING = {
  // Glow effects
  /** Inner glow radius multiplier */
  INNER_GLOW_RADIUS: 1.3,
  /** Outer glow radius multiplier */
  OUTER_GLOW_RADIUS: 2.2,

  // Sparkle effects
  /** Sparkles per food item */
  SPARKLE_COUNT: 4,
  /** Sparkle distance from center */
  SPARKLE_DISTANCE: 0.65,
  /** Sparkle size relative to food radius */
  SPARKLE_SIZE: 0.18,

  // Animations
  /** Shimmer animation speed */
  SHIMMER_SPEED: 5,
  /** Pulse animation speed */
  PULSE_SPEED: 3.5,
  /** Pulse amplitude (scale variation) */
  PULSE_AMPLITUDE: 0.12,
  /** Float/bobbing amplitude (pixels) */
  FLOAT_AMPLITUDE: 2.5,
  /** Float animation speed */
  FLOAT_SPEED: 2.5,
  /** Shine sweep speed */
  SHINE_SWEEP_SPEED: 1.5,
  /** Shine sweep width */
  SHINE_SWEEP_WIDTH: 0.6,
  /** Twinkle speed for sparkles */
  TWINKLE_SPEED: 8,
} as const;

export const FOOD_INNER_GLOW_RADIUS = FOOD_RENDERING.INNER_GLOW_RADIUS;
export const FOOD_OUTER_GLOW_RADIUS = FOOD_RENDERING.OUTER_GLOW_RADIUS;
export const FOOD_SPARKLE_COUNT = FOOD_RENDERING.SPARKLE_COUNT;
export const FOOD_SPARKLE_DISTANCE = FOOD_RENDERING.SPARKLE_DISTANCE;
export const FOOD_SPARKLE_SIZE = FOOD_RENDERING.SPARKLE_SIZE;
export const FOOD_SHIMMER_SPEED = FOOD_RENDERING.SHIMMER_SPEED;
export const FOOD_PULSE_SPEED = FOOD_RENDERING.PULSE_SPEED;
export const FOOD_PULSE_AMPLITUDE = FOOD_RENDERING.PULSE_AMPLITUDE;
export const FOOD_FLOAT_AMPLITUDE = FOOD_RENDERING.FLOAT_AMPLITUDE;
export const FOOD_FLOAT_SPEED = FOOD_RENDERING.FLOAT_SPEED;
export const FOOD_SHINE_SWEEP_SPEED = FOOD_RENDERING.SHINE_SWEEP_SPEED;
export const FOOD_SHINE_SWEEP_WIDTH = FOOD_RENDERING.SHINE_SWEEP_WIDTH;
export const FOOD_TWINKLE_SPEED = FOOD_RENDERING.TWINKLE_SPEED;

/**
 * Food shape types for variety - fun, colorful shapes like an arcade game
 */
export const FOOD_SHAPES = {
  CIRCLE: 0,      // Classic dot
  GEM: 1,         // Diamond gem
  STAR: 2,        // 4-pointed star
  HEXAGON: 3,     // Hexagon crystal
  HEART: 4,       // Heart shape
  FLOWER: 5,      // Flower with petals
  APPLE: 6,       // Apple shape
  CRESCENT: 7,    // Crescent moon
  COUNT: 8,
} as const;

export const FOOD_SHAPE_CIRCLE = FOOD_SHAPES.CIRCLE;
export const FOOD_SHAPE_GEM = FOOD_SHAPES.GEM;
export const FOOD_SHAPE_STAR = FOOD_SHAPES.STAR;
export const FOOD_SHAPE_HEXAGON = FOOD_SHAPES.HEXAGON;
export const FOOD_SHAPE_HEART = FOOD_SHAPES.HEART;
export const FOOD_SHAPE_FLOWER = FOOD_SHAPES.FLOWER;
export const FOOD_SHAPE_APPLE = FOOD_SHAPES.APPLE;
export const FOOD_SHAPE_CRESCENT = FOOD_SHAPES.CRESCENT;
export const FOOD_SHAPE_COUNT = FOOD_SHAPES.COUNT;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                    ⭐ POWERUP RENDERING - RETRO ARCADE                    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Powerup visual effects by rarity
 */
export const POWERUP_RENDERING = {
  // Glow intensity by rarity
  GLOW_COMMON: 0.45,
  GLOW_UNCOMMON: 0.65,
  GLOW_RARE: 1.0,

  // Rare item special effects
  RARE_GLOW_RADIUS: 3.5,
  RARE_PULSE_SPEED: 6,

  // Animations
  BOUNCE_AMPLITUDE: 4,
  BOUNCE_SPEED: 3,
} as const;

export const POWERUP_GLOW_COMMON = POWERUP_RENDERING.GLOW_COMMON;
export const POWERUP_GLOW_UNCOMMON = POWERUP_RENDERING.GLOW_UNCOMMON;
export const POWERUP_GLOW_RARE = POWERUP_RENDERING.GLOW_RARE;
export const POWERUP_RARE_GLOW_RADIUS = POWERUP_RENDERING.RARE_GLOW_RADIUS;
export const POWERUP_RARE_PULSE_SPEED = POWERUP_RENDERING.RARE_PULSE_SPEED;
export const POWERUP_BOUNCE_AMPLITUDE = POWERUP_RENDERING.BOUNCE_AMPLITUDE;
export const POWERUP_BOUNCE_SPEED = POWERUP_RENDERING.BOUNCE_SPEED;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                    🌟 BACKGROUND AMBIANCE - PLAYFUL WORLD                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Background ambiance settings for a lively, playful atmosphere
 */
export const AMBIANCE = {
  // Floating bubbles
  /** Number of bubbles floating in the world */
  BUBBLE_COUNT: 40,
  /** Minimum bubble rising speed */
  BUBBLE_SPEED_MIN: 15,
  /** Maximum bubble rising speed */
  BUBBLE_SPEED_MAX: 35,
  /** Minimum bubble size */
  BUBBLE_SIZE_MIN: 6,
  /** Maximum bubble size */
  BUBBLE_SIZE_MAX: 18,

  // Twinkling stars
  /** Number of stars scattered in the world */
  STAR_COUNT: 60,
  /** Star twinkle animation speed */
  STAR_TWINKLE_SPEED: 3,

  // Glowing orbs
  /** Number of floating orbs */
  ORB_COUNT: 15,
  /** Orb pulse animation speed */
  ORB_PULSE_SPEED: 2,

  // Dust particles
  /** Number of dust particles */
  DUST_COUNT: 80,
  /** Dust drift speed */
  DUST_SPEED: 8,
} as const;

export const AMBIANCE_BUBBLE_COUNT = AMBIANCE.BUBBLE_COUNT;
export const AMBIANCE_BUBBLE_SPEED_MIN = AMBIANCE.BUBBLE_SPEED_MIN;
export const AMBIANCE_BUBBLE_SPEED_MAX = AMBIANCE.BUBBLE_SPEED_MAX;
export const AMBIANCE_BUBBLE_SIZE_MIN = AMBIANCE.BUBBLE_SIZE_MIN;
export const AMBIANCE_BUBBLE_SIZE_MAX = AMBIANCE.BUBBLE_SIZE_MAX;
export const AMBIANCE_STAR_COUNT = AMBIANCE.STAR_COUNT;
export const AMBIANCE_STAR_TWINKLE_SPEED = AMBIANCE.STAR_TWINKLE_SPEED;
export const AMBIANCE_ORB_COUNT = AMBIANCE.ORB_COUNT;
export const AMBIANCE_ORB_PULSE_SPEED = AMBIANCE.ORB_PULSE_SPEED;
export const AMBIANCE_DUST_COUNT = AMBIANCE.DUST_COUNT;
export const AMBIANCE_DUST_SPEED = AMBIANCE.DUST_SPEED;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                    🍴 ENHANCED EATING EFFECTS                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Enhanced food eating feedback for satisfying gameplay
 */
export const EATING_EFFECTS = {
  /** Screen pulse intensity on eating (0-1) */
  SCREEN_PULSE_INTENSITY: 0.08,
  /** Screen pulse duration (seconds) */
  SCREEN_PULSE_DURATION: 0.12,
  /** Number of burst particles */
  BURST_PARTICLE_COUNT: 12,
  /** Burst particle speed range */
  BURST_SPEED_MIN: 100,
  BURST_SPEED_MAX: 200,
  /** Whether to spawn ring effect */
  RING_EFFECT_ENABLED: true,
  /** Ring expansion speed */
  RING_EXPANSION_SPEED: 300,
} as const;

export const EATING_SCREEN_PULSE_INTENSITY = EATING_EFFECTS.SCREEN_PULSE_INTENSITY;
export const EATING_SCREEN_PULSE_DURATION = EATING_EFFECTS.SCREEN_PULSE_DURATION;
export const EATING_BURST_PARTICLE_COUNT = EATING_EFFECTS.BURST_PARTICLE_COUNT;
export const EATING_BURST_SPEED_MIN = EATING_EFFECTS.BURST_SPEED_MIN;
export const EATING_BURST_SPEED_MAX = EATING_EFFECTS.BURST_SPEED_MAX;
export const EATING_RING_EFFECT_ENABLED = EATING_EFFECTS.RING_EFFECT_ENABLED;
export const EATING_RING_EXPANSION_SPEED = EATING_EFFECTS.RING_EXPANSION_SPEED;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                    💧 ESSENCE DEATH DROPS SYSTEM                          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Essence types dropped when worms die
 * Based on segment position modulo
 */
export enum EssenceType {
  /** Default drop - small blue droplet (+1 value) */
  SMALL = 'small',
  /** Every 5th segment - larger, brighter (+3 value) */
  MEDIUM = 'medium',
  /** Every 10th segment - glowing orb (+5 value) */
  LARGE = 'large',
}

/**
 * Essence configuration for death drops
 */
export const ESSENCE = {
  // Values
  /** Small essence value */
  SMALL_VALUE: 1,
  /** Medium essence value */
  MEDIUM_VALUE: 3,
  /** Large essence value */
  LARGE_VALUE: 5,

  // Segment intervals (1-indexed segment number)
  /** Every Nth segment becomes medium essence */
  MEDIUM_INTERVAL: 5,
  /** Every Nth segment becomes large essence */
  LARGE_INTERVAL: 10,

  // Colors - Blue theme for essence (neutral energy)
  /** Small essence - soft blue droplet */
  SMALL_COLOR: 0x4da6ff,
  /** Medium essence - brighter cyan-blue */
  MEDIUM_COLOR: 0x00d4ff,
  /** Large essence - glowing bright blue-white */
  LARGE_COLOR: 0x80ffff,

  // Sizes (multipliers of base food radius)
  /** Small essence size multiplier */
  SMALL_SIZE: 0.8,
  /** Medium essence size multiplier */
  MEDIUM_SIZE: 1.2,
  /** Large essence size multiplier */
  LARGE_SIZE: 1.6,

  // Glow effects
  /** Small essence glow intensity */
  SMALL_GLOW: 0.4,
  /** Medium essence glow intensity */
  MEDIUM_GLOW: 0.65,
  /** Large essence glow intensity */
  LARGE_GLOW: 1.0,

  // Animation
  /** Large essence pulse speed (faster = more attention-grabbing) */
  LARGE_PULSE_SPEED: 5,
  /** Large essence float amplitude (bigger bounce) */
  LARGE_FLOAT_AMPLITUDE: 4,
} as const;

export const ESSENCE_SMALL_VALUE = ESSENCE.SMALL_VALUE;
export const ESSENCE_MEDIUM_VALUE = ESSENCE.MEDIUM_VALUE;
export const ESSENCE_LARGE_VALUE = ESSENCE.LARGE_VALUE;
export const ESSENCE_MEDIUM_INTERVAL = ESSENCE.MEDIUM_INTERVAL;
export const ESSENCE_LARGE_INTERVAL = ESSENCE.LARGE_INTERVAL;
export const ESSENCE_SMALL_COLOR = ESSENCE.SMALL_COLOR;
export const ESSENCE_MEDIUM_COLOR = ESSENCE.MEDIUM_COLOR;
export const ESSENCE_LARGE_COLOR = ESSENCE.LARGE_COLOR;
export const ESSENCE_SMALL_SIZE = ESSENCE.SMALL_SIZE;
export const ESSENCE_MEDIUM_SIZE = ESSENCE.MEDIUM_SIZE;
export const ESSENCE_LARGE_SIZE = ESSENCE.LARGE_SIZE;
export const ESSENCE_SMALL_GLOW = ESSENCE.SMALL_GLOW;
export const ESSENCE_MEDIUM_GLOW = ESSENCE.MEDIUM_GLOW;
export const ESSENCE_LARGE_GLOW = ESSENCE.LARGE_GLOW;
export const ESSENCE_LARGE_PULSE_SPEED = ESSENCE.LARGE_PULSE_SPEED;
export const ESSENCE_LARGE_FLOAT_AMPLITUDE = ESSENCE.LARGE_FLOAT_AMPLITUDE;
